import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Apple, ArrowLeft, Monitor, RotateCw, Terminal } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge, TagChip, type BadgeTone } from '@/components/ui/Badge';
import { Alert } from '@/components/ui/Alert';
import { ApiErrorAlert } from '@/components/ui/ApiErrorAlert';
import { DataList } from '@/components/ui/DataList';
import { Skeleton, Spinner } from '@/components/ui/Feedback';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { Tabs, TabPanel } from '@/components/ui/Tabs';
import { DeviceActionBar } from './DeviceActionBar';
import { DeviceHardwarePanel } from './DeviceHardwarePanel';
import { DeviceActivityPanel } from './DeviceActivityPanel';
import { getDevice } from '@/lib/api/endpoints/devices';
import { queryKeys } from '@/lib/query-keys';
import { formatAbsolute, formatDuration, formatRelative } from '@/lib/format';
import type { DevicePlatform, DeviceStatus } from '@/lib/api/types';

/*
  Device detail.

  Replaces the placeholder anchor the device table used to link to (href="#device-{id}"), which
  went nowhere and could not be shared. The route is /devices/$deviceId, so the page is a URL a
  technician can paste into a ticket.

  Three states, each explicit:

  * pending: skeletons in the shape of the content, plus a live region that says what is
    loading. Not a bare spinner, and never a spinner with no resolution: the query has a retry
    policy and a terminal error state, and the error state is rendered.
  * error: ApiErrorAlert, which distinguishes 404 from 403 from a transport failure, because
    "this device was revoked and is gone" and "you cannot see this device" lead somewhere
    different. A 404 additionally offers the way back to the list.
  * loaded: header, actions, three tabs.

  The refetch interval is 20 seconds while the page is open, a little tighter than the list's
  30, because presence and the logged-in user are what a technician is watching before they
  decide to connect. It pauses when the tab is hidden, which TanStack does by default.

  The tab selection is component state rather than a URL parameter, unlike the device list's
  filters. The filters are the thing worth sharing about a list; which of three tabs of one
  device was last open is not, and putting it in the URL would add a validateSearch schema to
  the route for no gain. Revisit if a tab ever gets its own expensive query.
*/

const PLATFORM_ICON: Readonly<Record<DevicePlatform, typeof Monitor>> = {
  windows: Monitor,
  macos: Apple,
  linux: Terminal,
};

const STATUS_TONE: Readonly<Record<DeviceStatus, BadgeTone>> = {
  pending: 'info',
  active: 'success',
  revoked: 'danger',
  retired: 'neutral',
};

const STATUS_EXPLANATION: Readonly<Record<DeviceStatus, string>> = {
  pending: 'Enrolled but has never completed a gateway connection.',
  active: 'Holds a valid credential and may be connected to.',
  revoked: 'Credential invalidated. The agent can no longer authenticate.',
  retired: 'Uninstalled or decommissioned. Kept for the audit trail.',
};

export interface DeviceDetailPageProps {
  readonly deviceId: string;
}

export function DeviceDetailPage({ deviceId }: DeviceDetailPageProps): React.JSX.Element {
  const [tab, setTab] = useState('overview');

  const detail = useQuery({
    queryKey: queryKeys.device(deviceId),
    queryFn: () => getDevice(deviceId),
    refetchInterval: 20_000,
  });

  const backLink = (
    <Link
      to="/devices"
      className="inline-flex items-center gap-1.5 rounded text-sm font-medium text-accent hover:underline"
    >
      <ArrowLeft className="size-4" aria-hidden="true" />
      All devices
    </Link>
  );

  if (detail.isPending) {
    return (
      <div className="flex flex-col gap-5">
        {backLink}
        <p aria-live="polite" className="text-sm text-fg-muted">
          <Spinner label="Loading device" />
          Loading this device
        </p>
        <Skeleton className="h-8 w-72" />
        <Card className="p-4">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="mt-3 h-40 w-full" />
        </Card>
      </div>
    );
  }

  if (detail.isError) {
    return (
      <div className="flex flex-col gap-5">
        {backLink}
        <h1 className="text-xl font-semibold tracking-tight text-fg">Device</h1>
        <ApiErrorAlert
          error={detail.error}
          context="Could not load this device"
          onRetry={() => {
            void detail.refetch();
          }}
        />
      </div>
    );
  }

  const { device, presence, hardware } = detail.data;
  const PlatformIcon = PLATFORM_ICON[device.platform];
  const displayName = device.displayName ?? device.hostname;
  const state = presence?.state ?? 'offline';

  return (
    <div className="flex flex-col gap-5">
      {backLink}

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2.5">
            <PlatformIcon className="size-5 shrink-0 text-fg-subtle" aria-hidden="true" />
            <h1 className="font-mono text-xl font-semibold tracking-tight text-fg">
              {displayName}
            </h1>
            <StatusBadge state={state} />
            <Badge tone={STATUS_TONE[device.status]} title={STATUS_EXPLANATION[device.status]}>
              {device.status}
            </Badge>
          </div>
          <p className="mt-1 text-sm text-fg-muted">
            {detail.data.organizationName}
            {detail.data.siteName === null ? '' : ` / ${detail.data.siteName}`}
            {detail.data.deviceGroupName === null ? '' : ` / ${detail.data.deviceGroupName}`}
          </p>
          {device.tags.length > 0 ? (
            <p className="mt-2 flex flex-wrap gap-1">
              {device.tags.map((tag) => (
                <TagChip key={tag}>{tag}</TagChip>
              ))}
            </p>
          ) : null}
        </div>

        <div className="flex items-center gap-2">
          {detail.isFetching ? <Spinner label="Refreshing" /> : null}
          <Button
            variant="secondary"
            onClick={() => {
              void detail.refetch();
            }}
            loading={detail.isFetching}
          >
            <RotateCw className="size-4" aria-hidden="true" />
            Refresh
          </Button>
        </div>
      </div>

      {device.status === 'revoked' ? (
        <Alert tone="danger" title="This device is revoked" liveness="polite">
          Its credential was invalidated
          {device.revokedAt === null ? '' : ` ${formatRelative(device.revokedAt)}`} and it can no
          longer authenticate to the gateway.
          {device.revokedReason === null ? '' : ` Reason given: ${device.revokedReason}`} The agent
          binaries may still be installed on the machine; revocation does not uninstall anything.
        </Alert>
      ) : null}

      {device.status === 'pending' ? (
        <Alert tone="warning" title="Enrollment has not completed" liveness="polite">
          An enrollment token was redeemed for this device but the gateway has never seen it
          connect. There is no presence and no inventory yet. Check that the agent service is
          running and that it can reach the gateway on 443.
        </Alert>
      ) : null}

      <DeviceActionBar
        device={device}
        presence={presence}
        organizationName={detail.data.organizationName}
      />

      <Card>
        <Tabs
          label="Device detail sections"
          value={tab}
          onValueChange={setTab}
          tabs={[
            { value: 'overview', label: 'Overview' },
            { value: 'hardware', label: 'Hardware' },
            {
              value: 'activity',
              label: 'Activity',
              badge:
                detail.data.recentSessions.length +
                detail.data.technicianActivity.length +
                detail.data.administrativeActivity.length,
            },
          ]}
        >
          <TabPanel value="overview">
            <div className="flex flex-col gap-4">
              <section aria-labelledby="overview-identity">
                <h3
                  id="overview-identity"
                  className="mb-3 text-xs font-semibold tracking-wide text-fg-muted uppercase"
                >
                  Identity
                </h3>
                <DataList
                  columns={3}
                  items={[
                    { label: 'Hostname', value: device.hostname, mono: true },
                    {
                      label: 'Display name',
                      value: device.displayName,
                      emptyText: 'Same as hostname',
                    },
                    { label: 'Manufacturer', value: device.manufacturer },
                    { label: 'Model', value: device.model },
                    { label: 'Serial number', value: device.serialNumber, mono: true },
                    { label: 'Machine GUID', value: device.machineGuid, mono: true },
                  ]}
                />
              </section>

              <hr className="border-border" />

              <section aria-labelledby="overview-os">
                <h3
                  id="overview-os"
                  className="mb-3 text-xs font-semibold tracking-wide text-fg-muted uppercase"
                >
                  Operating system
                </h3>
                <DataList
                  columns={3}
                  items={[
                    { label: 'Product', value: device.osProduct },
                    {
                      label: 'Version and build',
                      value:
                        device.osVersion === null && device.osBuild === null
                          ? null
                          : `${device.osVersion ?? 'Unknown'}${
                              device.osBuild === null ? '' : ` (build ${device.osBuild})`
                            }`,
                    },
                    { label: 'Architecture', value: device.osArch, mono: true },
                    {
                      label: 'Domain or workgroup',
                      value: device.domainOrWorkgroup,
                      mono: true,
                      emptyText: 'Not joined',
                    },
                    {
                      label: 'Platform',
                      value: device.platform === 'macos' ? 'macOS' : device.platform,
                    },
                  ]}
                />
              </section>

              <hr className="border-border" />

              <section aria-labelledby="overview-presence">
                <h3
                  id="overview-presence"
                  className="mb-3 text-xs font-semibold tracking-wide text-fg-muted uppercase"
                >
                  Presence and connectivity
                </h3>
                <DataList
                  columns={3}
                  items={[
                    {
                      label: 'Logged-in user',
                      value:
                        presence?.loggedInUser === null || presence?.loggedInUser === undefined
                          ? null
                          : `${presence.loggedInUser}${
                              presence.userSessionState === null
                                ? ''
                                : ` (${presence.userSessionState.replace('_', ' ')})`
                            }`,
                      mono: true,
                      emptyText: 'No interactive user',
                    },
                    {
                      label: 'Uptime',
                      value:
                        presence?.lastBootAt === null || presence?.lastBootAt === undefined
                          ? null
                          : formatDuration(presence.lastBootAt, null),
                      emptyText: 'Unknown',
                    },
                    {
                      label: 'Last boot',
                      value:
                        presence?.lastBootAt === null || presence?.lastBootAt === undefined ? null : (
                          <time dateTime={presence.lastBootAt} title={formatAbsolute(presence.lastBootAt)}>
                            {formatRelative(presence.lastBootAt)}
                          </time>
                        ),
                    },
                    { label: 'Local IP', value: presence?.localIp ?? null, mono: true },
                    { label: 'Public IP', value: presence?.publicIp ?? null, mono: true },
                    {
                      label: 'Last check-in',
                      value:
                        presence?.lastSeenAt === null || presence?.lastSeenAt === undefined ? null : (
                          <time dateTime={presence.lastSeenAt} title={formatAbsolute(presence.lastSeenAt)}>
                            {formatRelative(presence.lastSeenAt)}
                          </time>
                        ),
                      emptyText: 'Never',
                    },
                    {
                      label: 'Connected since',
                      value:
                        presence?.connectedSince === null || presence?.connectedSince === undefined ? null : (
                          <time
                            dateTime={presence.connectedSince}
                            title={formatAbsolute(presence.connectedSince)}
                          >
                            {formatRelative(presence.connectedSince)}
                          </time>
                        ),
                      emptyText: 'Not connected',
                    },
                    {
                      label: 'Gateway node',
                      value: presence?.gatewayNode ?? null,
                      mono: true,
                      emptyText: 'Not connected',
                    },
                  ]}
                />
              </section>

              <hr className="border-border" />

              <section aria-labelledby="overview-agent">
                <h3
                  id="overview-agent"
                  className="mb-3 text-xs font-semibold tracking-wide text-fg-muted uppercase"
                >
                  Agent and enrollment
                </h3>
                <DataList
                  columns={3}
                  items={[
                    { label: 'Agent version', value: device.agentVersion, mono: true },
                    { label: 'Update channel', value: device.agentChannel },
                    {
                      label: 'Policy revision',
                      value: presence?.policyRevision ?? null,
                      mono: true,
                    },
                    {
                      label: 'Enrolled',
                      value:
                        device.enrolledAt === null ? null : (
                          <time dateTime={device.enrolledAt} title={formatAbsolute(device.enrolledAt)}>
                            {formatRelative(device.enrolledAt)}
                          </time>
                        ),
                      emptyText: 'Enrollment not completed',
                    },
                    {
                      label: 'Record created',
                      value: (
                        <time dateTime={device.createdAt} title={formatAbsolute(device.createdAt)}>
                          {formatRelative(device.createdAt)}
                        </time>
                      ),
                    },
                    {
                      label: 'Record updated',
                      value: (
                        <time dateTime={device.updatedAt} title={formatAbsolute(device.updatedAt)}>
                          {formatRelative(device.updatedAt)}
                        </time>
                      ),
                    },
                  ]}
                />
              </section>

              {device.notes === null ? null : (
                <>
                  <hr className="border-border" />
                  <section aria-labelledby="overview-notes">
                    <h3
                      id="overview-notes"
                      className="mb-2 text-xs font-semibold tracking-wide text-fg-muted uppercase"
                    >
                      Notes
                    </h3>
                    <p className="text-sm whitespace-pre-line text-fg">{device.notes}</p>
                  </section>
                </>
              )}
            </div>
          </TabPanel>

          <TabPanel value="hardware">
            <DeviceHardwarePanel hardware={hardware} />
          </TabPanel>

          <TabPanel value="activity">
            <DeviceActivityPanel
              hostname={device.hostname}
              sessions={detail.data.recentSessions}
              technicianActivity={detail.data.technicianActivity}
              administrativeActivity={detail.data.administrativeActivity}
            />
          </TabPanel>
        </Tabs>
      </Card>
    </div>
  );
}
